/**
 * Сцена видео-референса (спека §12): опорный кадр (клик — плеер на `key_ms`), таймкоды и длина будущего
 * кадра, «использовать», бейджи, описание композером, комментарий (автосохранение — у экрана) и меню «…»
 * с правками по позиции плеера: опорный кадр, разделить, склеить со следующей, удалить.
 */
import { ImageOff, MoreHorizontal } from "lucide-react";
import { useState } from "react";

import { btnGhost } from "@/components/trends/ui";
import { thumbUrl } from "@/lib/media";
import { canSplit, fmtTime, insideScene, sceneBadges, sceneTitle, type SceneDesc, type VideoRefScene } from "@/lib/videoRef";

const FRAMING: Record<string, string> = { medium: "средний", wide: "общий", back: "со спины", detail: "деталь" };
const TRANSITION: Record<string, string> = { cut: "склейка", crossfade: "наплыв", other: "другой переход" };

function DescLine({ desc }: { desc: SceneDesc }) {
  const parts = [
    desc.framing ? (FRAMING[desc.framing] ?? desc.framing) : null,
    desc.camera,
    desc.action,
    desc.dress_focus ? `акцент: ${desc.dress_focus}` : null,
    desc.transition ? (TRANSITION[desc.transition] ?? desc.transition) : null,
  ].filter(Boolean);
  return (
    <div className="text-[12px] text-fg-muted">
      {parts.join(" · ")}
      {desc.on_screen_text ? <span className="ml-1 text-warn">текст на экране: {desc.on_screen_text}</span> : null}
    </div>
  );
}

export function SceneCard({
  scene,
  playerMs,
  minSceneMs,
  cuts,
  note,
  editable,
  busy,
  hasNext,
  onMark,
  onNote,
  onKey,
  onSplit,
  onMerge,
  onDrop,
  onSeek,
}: {
  scene: VideoRefScene;
  playerMs: number;
  minSceneMs: number;
  /** Склейки разбора — для бейджа «склеено». */
  cuts: { ms: number }[] | null | undefined;
  /** Текст поля комментария (своя правка поверх сервера). */
  note: string;
  /** Референс `ready`: правки разрешены. */
  editable: boolean;
  /** Идёт правка сцен — меню и галочка ждут. */
  busy: boolean;
  hasNext: boolean;
  onMark: (mark: "use" | "skip") => void;
  onNote: (text: string) => void;
  onKey: (ms: number) => void;
  onSplit: (ms: number) => void;
  onMerge: () => void;
  onDrop: () => void;
  onSeek: (ms: number) => void;
}) {
  const [menu, setMenu] = useState(false);
  const [confirmDrop, setConfirmDrop] = useState(false);
  const used = scene.mark === "use";
  const badges = sceneBadges(scene, cuts);
  const keyOk = insideScene(scene, playerMs);
  const splitOk = canSplit(scene, playerMs, minSceneMs);
  const act = (fn: () => void) => () => {
    setMenu(false);
    setConfirmDrop(false);
    fn();
  };

  return (
    <div
      className={
        "flex gap-3 rounded-[var(--radius-card)] border border-border bg-bg p-2 " + (used ? "" : "opacity-60")
      }
      data-annot={`видео-референс: сцена ${scene.n}`}
    >
      <button
        type="button"
        onClick={() => onSeek(scene.key_ms)}
        title={`к опорному кадру ${fmtTime(scene.key_ms)}`}
        className="h-[128px] w-[72px] flex-none overflow-hidden rounded-[4px] bg-bg-subtle"
      >
        {scene.key_path ? (
          <img src={thumbUrl(scene.key_path)} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-fg-faint">
            <ImageOff size={16} />
          </span>
        )}
      </button>

      <div className="grid min-w-0 flex-1 content-start gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-[12.5px] text-fg">
            <input
              type="checkbox"
              checked={used}
              disabled={!editable || busy}
              onChange={(e) => onMark(e.target.checked ? "use" : "skip")}
            />
            использовать
          </label>
          <span className="text-[12px] text-fg-muted tnum">{sceneTitle(scene)}</span>
          {badges.map((b) => (
            <span key={b} className="rounded-[3px] border border-border bg-bg-subtle px-1.5 text-[11px] text-fg-faint">
              {b}
            </span>
          ))}
          <div className="relative ml-auto">
            <button
              type="button"
              className={btnGhost}
              aria-label="действия со сценой"
              disabled={!editable || busy}
              onClick={() => {
                setMenu((v) => !v);
                setConfirmDrop(false);
              }}
            >
              <MoreHorizontal size={14} />
            </button>
            {menu ? (
              <div className="absolute right-0 top-8 z-10 grid w-[260px] gap-0.5 rounded-[var(--radius-ui)] border border-border bg-bg p-1 shadow-lg">
                <MenuItem
                  disabled={!keyOk}
                  hint={keyOk ? fmtTime(playerMs) : "позиция плеера вне сцены"}
                  onClick={act(() => onKey(playerMs))}
                >
                  кадр = текущий момент плеера
                </MenuItem>
                <MenuItem
                  disabled={!splitOk}
                  hint={splitOk ? fmtTime(playerMs) : "плеер внутри сцены, 0,7 с от краёв"}
                  onClick={act(() => onSplit(playerMs))}
                >
                  разделить здесь
                </MenuItem>
                <MenuItem disabled={!hasNext} hint={hasNext ? undefined : "последняя сцена"} onClick={act(onMerge)}>
                  склеить со следующей
                </MenuItem>
                {confirmDrop ? (
                  <MenuItem danger onClick={act(onDrop)} hint="дыра в ролике останется">
                    точно удалить
                  </MenuItem>
                ) : (
                  <MenuItem danger onClick={() => setConfirmDrop(true)}>
                    удалить
                  </MenuItem>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {scene.desc ? <DescLine desc={scene.desc} /> : null}

        <textarea
          value={note}
          disabled={!editable}
          maxLength={500}
          rows={2}
          placeholder="комментарий к сцене — уйдёт композеру"
          aria-label={`комментарий к сцене ${scene.n}`}
          onChange={(e) => onNote(e.target.value)}
          className="min-h-[44px] w-full resize-y rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1 text-[12.5px] text-fg outline-none placeholder:text-fg-faint focus:border-accent disabled:opacity-60"
        />
      </div>
    </div>
  );
}

function MenuItem({
  children,
  hint,
  disabled = false,
  danger = false,
  onClick,
}: {
  children: string;
  hint?: string | undefined;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={
        "flex items-baseline gap-2 rounded-[3px] px-2 py-1 text-left text-[12.5px] hover:bg-bg-hover disabled:cursor-not-allowed disabled:opacity-50 " +
        (danger ? "text-err" : "text-fg")
      }
    >
      {children}
      {hint ? <span className="ml-auto text-[11px] text-fg-faint">{hint}</span> : null}
    </button>
  );
}
